// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { getErrorMessage } from '@/utils/errors';

import { RootState } from '../index';

const DEBUG = false;
const apiUrl = window.config.userApiUrl + '/casino-instance/acquisition-budget';

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const getAuthHeaders = (state: RootState): Record<string, string> | null => {
  const authToken = state.authentication.access_token;
  if (isNil(authToken)) return null;
  return { Authorization: authToken };
};

/* ---------- API response shapes ---------- */

/**
 * Acquisition budget for a single casino instance (brand). The POST body is this flat
 * record. The GET response is an ENVELOPE that nests this same record under an
 * `acquisition_budget` key, alongside the backing wallet objects:
 *
 *   {
 *     "acquisition_budget": { acquisition_budget, daily_acquisition_budget, budget_details },
 *     "acquisition_wallet": { wallet_type: "ACQUISITION", balance, ... },
 *     "daily_acquisition_wallet": { wallet_type: "DAILY_ACQUISITION", balance, ... }
 *   }
 *
 * `unwrapBudget` flattens both shapes to this record. `budget_details` is a free-form
 * bag (per-channel splits keyed by the `aft` literals), so it stays an open record.
 */
export interface AcquisitionBudget {
  casino_instance_id: string;
  /** Total lifetime acquisition budget. */
  acquisition_budget: number;
  /** Per-day cap. */
  daily_acquisition_budget: number;
  budget_details: Record<string, any> | null;
}

/* ---------- thunks ----------

Endpoints (require an `Authorization` header carrying the access token):
  GET  {userApiUrl}/casino-instance/acquisition-budget?casino_instance_id=<id>
  POST {userApiUrl}/casino-instance/acquisition-budget
       { casino_instance_id, acquisition_budget, daily_acquisition_budget, budget_details }

State is keyed by `casino_instance_id` so budgets for several brands can coexist
(the owner can switch brands without clobbering the previously-loaded budget).
*/

const unwrapBudget = (payload: any): Partial<AcquisitionBudget> => {
  if (isNil(payload) || typeof payload !== 'object') return {};

  // GET envelope: the record is NESTED under `acquisition_budget`, served next to the
  // wallet objects. The flat POST record carries `acquisition_budget` as a NUMBER, so an
  // OBJECT here means this is the wrapper, so descend into the nested record.
  const nested = payload.acquisition_budget;
  if (!isNil(nested) && typeof nested === 'object') {
    return nested as Partial<AcquisitionBudget>;
  }

  // Flat record (POST body / echo): the scalar `acquisition_budget` / `casino_instance_id`
  // keys mean the payload IS the record.
  if ('acquisition_budget' in payload || 'casino_instance_id' in payload) {
    return payload as Partial<AcquisitionBudget>;
  }

  // `{ data: {...} }` wrapper: descend (the inner object may itself be flat or nested).
  if ('data' in payload && !isNil(payload.data) && typeof payload.data === 'object') {
    return unwrapBudget(payload.data);
  }

  return payload as Partial<AcquisitionBudget>;
};

export const fetchAcquisitionBudget = createAsyncThunk<
  { casinoInstanceId: string; data: Partial<AcquisitionBudget> },
  { casinoInstanceId: string },
  GenericError
>('acquisitionBudget/fetch', async ({ casinoInstanceId }, { rejectWithValue, getState }) => {
  try {
    const headers = getAuthHeaders(getState() as RootState);
    if (!headers) return rejectWithValue({ error: 'No authentication token found' });
    if (isNil(casinoInstanceId)) return rejectWithValue({ error: 'casinoInstanceId is required' });

    const response = await axios.get(apiUrl, {
      headers,
      params: { casino_instance_id: casinoInstanceId },
    });
    DEBUG && console.log('[AcquisitionBudget Slice] fetch response', response);

    if (!isNil(response.data)) return { casinoInstanceId, data: unwrapBudget(response.data) };
    // 200 with an empty body = this brand simply has no budget yet. Resolve with an EMPTY
    // record (not a rejection) so the slice still writes a per-brand entry — see below.
    return { casinoInstanceId, data: {} };
  } catch (error: any) {
    // A 404 is the clean "this brand has no budget yet" signal: resolve with an empty
    // record so the reducer still writes a per-brand entry. The Budget & Limits modal
    // gates the first save on that entry existing (`budgetReady` in AdBudgetLimit), so
    // without this the user could never SET an initial daily limit for a fresh brand
    // (acquisition_budget defaults to 0 on that first save). Any other error (incl. a
    // 5xx, e.g. the backend's missing-wallet `wallet_types.findUniqueOrThrow` 500) keeps
    // rejecting: save stays disabled because a save would fail server-side too, and the
    // no-clobber guard stays intact (we never POST acquisition_budget=0 over a budget we
    // merely failed to read).
    if (error?.response?.status === 404) {
      return { casinoInstanceId, data: {} };
    }
    return rejectWithValue({ error: getErrorMessage(error) });
  }
});

export type SaveAcquisitionBudgetProps = {
  casinoInstanceId: string;
  acquisitionBudget: number;
  dailyAcquisitionBudget: number;
  budgetDetails?: Record<string, any> | null;
};

export const saveAcquisitionBudget = createAsyncThunk<
  { casinoInstanceId: string; data: AcquisitionBudget },
  SaveAcquisitionBudgetProps,
  GenericError
>(
  'acquisitionBudget/save',
  async ({ casinoInstanceId, acquisitionBudget, dailyAcquisitionBudget, budgetDetails = null }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });
      if (isNil(casinoInstanceId)) return rejectWithValue({ error: 'casinoInstanceId is required' });

      const body: AcquisitionBudget = {
        casino_instance_id: casinoInstanceId,
        acquisition_budget: acquisitionBudget,
        daily_acquisition_budget: dailyAcquisitionBudget,
        budget_details: budgetDetails,
      };

      const response = await axios.post(apiUrl, body, { headers });
      DEBUG && console.log('[AcquisitionBudget Slice] save response', response);

      // The save is the source of truth for the new values; merge any server
      // echo on top so server-derived fields (if any) win, but never lose what
      // we just set.
      const merged: AcquisitionBudget = { ...body, ...unwrapBudget(response.data) };
      return { casinoInstanceId, data: merged };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/* ---------- slice state ---------- */

export interface AcquisitionBudgetState {
  /** Loaded budgets keyed by `casino_instance_id`. */
  byCasinoInstance: Record<string, Partial<AcquisitionBudget>>;

  /** GET request status. */
  loading: boolean;
  loaded: boolean;
  error: string | null | undefined;

  /** POST request status. */
  saving: boolean;
  saveError: string | null | undefined;
}

const initialState: AcquisitionBudgetState = {
  byCasinoInstance: {},

  loading: false,
  loaded: false,
  error: null,

  saving: false,
  saveError: null,
};

export const acquisitionBudgetSlice = createSlice({
  name: 'acquisitionBudget',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      /* ----- GET acquisition-budget ----- */
      .addCase(fetchAcquisitionBudget.pending, (state) => {
        state.loading = true;
        state.loaded = false;
        state.error = null;
      })
      .addCase(fetchAcquisitionBudget.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.byCasinoInstance[action.payload.casinoInstanceId] = {
          casino_instance_id: action.payload.casinoInstanceId,
          ...action.payload.data,
        };
      })
      .addCase(fetchAcquisitionBudget.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload?.error;
      })

      /* ----- POST acquisition-budget ----- */
      .addCase(saveAcquisitionBudget.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(saveAcquisitionBudget.fulfilled, (state, action) => {
        state.saving = false;
        state.byCasinoInstance[action.payload.casinoInstanceId] = action.payload.data;
      })
      .addCase(saveAcquisitionBudget.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload?.error;
      });
  },
});

/* ---------- selectors ---------- */

/** The loaded budget for a given casino instance, or `undefined` if not fetched. */
export const selectAcquisitionBudget = (
  state: RootState,
  casinoInstanceId: string | undefined | null,
): Partial<AcquisitionBudget> | undefined =>
  isNil(casinoInstanceId) ? undefined : state.acquisitionBudget.byCasinoInstance[casinoInstanceId];

export default acquisitionBudgetSlice.reducer;
